import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import { EdcForm } from "@/features/tender/EdcForm";
import type { TenderPart } from "@/features/tender/useTender";
import { call } from "@/lib/api";
import type { PickupComplete } from "@/lib/types";

export interface PickupPayment {
  method: "cash" | "edc_external";
  part: TenderPart;
}

export function emptyPickupPayment(): PickupPayment {
  return {
    method: "cash",
    part: {
      id: "pickup",
      method: "cash",
      amount: "",
      tendered: "",
      terminalId: "",
      cardBrand: "",
      approvalCode: "",
      last4: "",
    },
  };
}

/** Is the payment filled in enough to send (the server checks the amounts again)? */
export function pickupPaymentReady(payment: PickupPayment, amountDue: number): boolean {
  if (amountDue <= 0) {
    return true;
  }
  if (payment.method === "cash") {
    const tendered = payment.part.tendered ? Number.parseInt(payment.part.tendered, 10) : amountDue;
    return tendered >= amountDue;
  }
  return payment.part.approvalCode.trim().length > 0 && payment.part.cardBrand !== "";
}

/** The `payments[]` of `POST /pos/pickups/{orderId}/complete` — the amount is the server's 残額. */
export function pickupPayments(
  payment: PickupPayment,
  amountDue: number,
  defaultTerminal: string | null,
): NonNullable<PickupComplete["payments"]> {
  if (amountDue <= 0) {
    return [];
  }
  if (payment.method === "cash") {
    return [
      {
        method: "cash",
        amount: amountDue,
        ...(payment.part.tendered ? { tendered: Number.parseInt(payment.part.tendered, 10) } : {}),
      },
    ];
  }
  const terminalId = payment.part.terminalId || defaultTerminal;
  return [
    {
      method: "edc_external",
      amount: amountDue,
      ...(terminalId ? { terminalId } : {}),
      cardBrand: payment.part.cardBrand,
      approvalCode: payment.part.approvalCode.trim(),
      ...(payment.part.last4 ? { last4: payment.part.last4 } : {}),
    },
  ];
}

/**
 * 店頭払い (T07-29): the web order's 残額 in cash (お預かり on the pad — the change comes back from
 * the server) or on the shop's card terminal (承認番号). One way of paying — a split is done at
 * the お会計 screen of a counter sale.
 */
export function PickupPay({
  amountDue,
  payment,
  onChange,
}: {
  amountDue: number;
  payment: PickupPayment;
  onChange: (payment: PickupPayment) => void;
}) {
  const { t } = useTranslation();
  const { api } = usePos();
  const methods = useQuery({
    queryKey: ["payment-methods", "list", "pos"],
    queryFn: () => call(() => api.GET("/v1/pos/payment-methods")),
    staleTime: 60_000,
  });
  const set = (changes: Partial<TenderPart>) =>
    onChange({ ...payment, part: { ...payment.part, ...changes } });
  const tendered = payment.part.tendered ? Number.parseInt(payment.part.tendered, 10) : null;
  return (
    <section aria-labelledby="pickup-pay" className="flex flex-col gap-3 rounded-md border p-3">
      <h2 id="pickup-pay" className="flex items-baseline justify-between text-lg font-semibold">
        {t("pickup.amount_due")}
        <MoneyText amount={amountDue} size="xl" data-testid="pickup-due" />
      </h2>
      <div className="grid grid-cols-2 gap-2">
        {(["cash", "edc_external"] as const).map((method) => (
          <button
            key={method}
            type="button"
            aria-pressed={payment.method === method}
            onClick={() => onChange({ ...payment, method, part: { ...payment.part, method } })}
            className="min-h-pos-touch rounded-md border text-lg font-semibold aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
          >
            {method === "cash" ? t("tender.method_cash") : t("tender.method_card")}
          </button>
        ))}
      </div>
      {payment.method === "cash" ? (
        <div className="grid grid-cols-[1fr_14rem] gap-3">
          <NumPad
            label={t("tender.tendered")}
            value={payment.part.tendered}
            onChange={(digits) => set({ tendered: digits })}
            money
            maxLength={9}
          />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              aria-pressed={!payment.part.tendered}
              onClick={() => set({ tendered: "" })}
              className="min-h-pos-touch rounded-md border text-lg aria-pressed:border-primary aria-pressed:bg-accent"
            >
              {t("tender.exact")} <MoneyText amount={amountDue} />
            </button>
            <p className="text-sm text-muted-foreground">{t("pickup.change_after")}</p>
            {tendered !== null && tendered < amountDue ? (
              <p role="alert" className="text-pos-problem">
                {t("tender.not_enough")}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <EdcForm
          part={payment.part}
          index={0}
          methods={methods.data}
          preview={undefined}
          onChange={set}
        />
      )}
    </section>
  );
}
